/**
 * PDP gallery slider — slides + thumbnails + swipe / drag
 */
(function () {
  'use strict';

  const SWIPE_THRESHOLD = 48;

  function imagePath(url) {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).pathname;
    } catch {
      return String(url).split('?')[0];
    }
  }

  function shopifySizedUrl(url, width) {
    if (!url) return '';
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.href;
    } catch {
      return String(url);
    }
  }

  function setActiveThumb(root, activeThumb) {
    const thumbs = root.querySelectorAll('[data-gallery-thumb][data-slide-index]');
    thumbs.forEach((t) => {
      const on = activeThumb ? t === activeThumb : false;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.setAttribute('tabindex', on ? '0' : '-1');
    });
  }

  function scrollThumbIntoView(thumb) {
    if (!thumb) return;
    thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  function initGallery(root) {
    const track = root.querySelector('[data-gallery-track]');
    const viewport = root.querySelector('[data-gallery-viewport]');
    const slides = root.querySelectorAll('[data-gallery-slide]');
    const thumbs = root.querySelectorAll('[data-gallery-thumb][data-slide-index]');
    const prevBtn = root.querySelector('[data-gallery-prev]');
    const nextBtn = root.querySelector('[data-gallery-next]');
    const live = root.querySelector('[data-gallery-live]');
    const mainWrap = root.querySelector('[data-gallery-main]');

    if (!track || !viewport || slides.length === 0) return;

    let index = 0;
    slides.forEach((slide, i) => {
      if (slide.classList.contains('is-active')) index = i;
    });

    const count = slides.length;
    let dragging = false;
    let startX = 0;
    let currentX = 0;
    let pointerId = null;

    function imageStem(url) {
      const path = imagePath(url);
      const base = path.split('/').pop() || '';
      return base.replace(/_\d+x(\.|$)/, '.').replace(/\.[^.]+$/, '');
    }

    function setSlideImage(slideIndex, imageUrl) {
      const slide = slides[slideIndex];
      const img = slide && slide.querySelector('[data-gallery-slide-img]');
      if (!img || !imageUrl) return;

      img.src = shopifySizedUrl(imageUrl, 1200);
      img.srcset = [400, 800, 1200]
        .map((w) => `${shopifySizedUrl(imageUrl, w)} ${w}w`)
        .join(', ');

      const thumb = thumbs[slideIndex];
      const thumbImg = thumb && thumb.querySelector('img');
      if (thumbImg) thumbImg.src = shopifySizedUrl(imageUrl, 160);
    }

    function applyGalleryImage(imageUrl, mediaId) {
      if (!imageUrl) return;
      const path = imagePath(imageUrl);
      const stem = imageStem(imageUrl);
      let matchIndex = -1;

      slides.forEach((slide, i) => {
        if (mediaId && slide.dataset.mediaId === String(mediaId)) {
          matchIndex = i;
          return;
        }
        const img = slide.querySelector('[data-gallery-slide-img]');
        if (!img) return;
        const src = img.currentSrc || img.src;
        if (src === imageUrl || imagePath(src) === path) {
          matchIndex = i;
          return;
        }
        if (stem && imageStem(src) === stem) matchIndex = i;
      });

      if (matchIndex >= 0) {
        goTo(matchIndex);
        return;
      }

      const variantSlide = root.querySelector('[data-variant-slide]');
      const variantIndex = variantSlide
        ? parseInt(variantSlide.getAttribute('data-slide-index'), 10)
        : 0;
      if (!Number.isNaN(variantIndex) && variantIndex >= 0) {
        setSlideImage(variantIndex, imageUrl);
        if (mediaId && variantSlide) variantSlide.dataset.mediaId = String(mediaId);
        goTo(variantIndex);
      }
    }

    function shouldHandleGalleryEvent(e) {
      const scopedGallery = e.detail && e.detail.gallery;
      if (scopedGallery && scopedGallery !== root) return false;
      const hero = root.closest('.cb-pdp-hero');
      if (!hero) return true;
      const picker = document.querySelector('[data-variant-picker]');
      if (picker && !hero.contains(picker)) return false;
      return true;
    }

    function pauseGalleryMedia() {
      slides.forEach((slide) => {
        slide.querySelectorAll('video').forEach((video) => {
          try {
            video.pause();
          } catch (_) { /* ignore */ }
        });
      });
    }

    function clearZoom() {
      if (mainWrap) mainWrap.classList.remove('is-zoomed');
      slides.forEach((slide) => {
        const img = slide.querySelector('[data-gallery-slide-img]');
        if (img) img.style.transformOrigin = '';
      });
    }

    function slideLabel(slide, position) {
      const type = slide && slide.getAttribute('data-media-type');
      const kind = type === 'video' || type === 'external_video' ? 'Video' : 'Image';
      return `${kind} ${position} of ${count}`;
    }

    function slideWidth() {
      return viewport.clientWidth || 1;
    }

    function setTrackTransform(offsetPx, animate) {
      track.style.transition = animate ? '' : 'none';
      const base = -index * slideWidth();
      track.style.transform = `translate3d(${base + offsetPx}px, 0, 0)`;
    }

    function updateNav() {
      const single = count <= 1;
      if (prevBtn) {
        prevBtn.hidden = single;
        prevBtn.disabled = index <= 0;
      }
      if (nextBtn) {
        nextBtn.hidden = single;
        nextBtn.disabled = index >= count - 1;
      }
    }

    function goTo(nextIndex, options) {
      const focusThumb = options && options.focusThumb;
      if (count === 0) return;
      index = Math.max(0, Math.min(nextIndex, count - 1));

      track.style.setProperty('--gallery-index', String(index));
      setTrackTransform(0, true);

      slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      const thumb = thumbs[index] || null;
      setActiveThumb(root, thumb);
      if (focusThumb && thumb) {
        thumb.focus();
      }
      scrollThumbIntoView(thumb);

      pauseGalleryMedia();
      clearZoom();
      updateNav();

      if (live) {
        live.textContent = slideLabel(slides[index], index + 1);
      }

      root.dispatchEvent(
        new CustomEvent('gwong:gallery:slide', { bubbles: true, detail: { index, slide: slides[index] } })
      );
    }

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      viewport.classList.remove('is-dragging');
      try {
        if (pointerId !== null) viewport.releasePointerCapture(pointerId);
      } catch (_) { /* ignore */ }
      pointerId = null;

      const delta = currentX - startX;
      if (delta <= -SWIPE_THRESHOLD) goTo(index + 1);
      else if (delta >= SWIPE_THRESHOLD) goTo(index - 1);
      else goTo(index);
    }

    root.addEventListener('click', (e) => {
      const thumb = e.target.closest('[data-gallery-thumb][data-slide-index]');
      if (thumb && root.contains(thumb)) {
        e.preventDefault();
        const i = parseInt(thumb.getAttribute('data-slide-index'), 10);
        if (!Number.isNaN(i)) goTo(i, { focusThumb: true });
        return;
      }
      if (e.target.closest('[data-gallery-prev]')) {
        e.preventDefault();
        goTo(index - 1);
        return;
      }
      if (e.target.closest('[data-gallery-next]')) {
        e.preventDefault();
        goTo(index + 1);
      }
    });

    const onKeydown = (e) => {
      const focused = document.activeElement;
      if (focused !== viewport && !root.contains(focused)) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goTo(index - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goTo(index + 1);
      }
    };

    root.addEventListener('keydown', onKeydown);
    viewport.addEventListener('keydown', onKeydown);

    if (count > 1) {
      viewport.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        if (e.target.closest('[data-gallery-prev], [data-gallery-next], video, .cb-pdp-gallery__video-wrap')) return;
        dragging = true;
        pointerId = e.pointerId;
        startX = e.clientX;
        currentX = startX;
        viewport.classList.add('is-dragging');
        viewport.setPointerCapture(pointerId);
        setTrackTransform(0, false);
      });

      viewport.addEventListener('pointermove', (e) => {
        if (!dragging || e.pointerId !== pointerId) return;
        currentX = e.clientX;
        const delta = currentX - startX;
        if (Math.abs(delta) > 8) e.preventDefault();
        setTrackTransform(delta, false);
      });

      viewport.addEventListener('pointerup', (e) => {
        if (e.pointerId !== pointerId) return;
        endDrag();
      });

      viewport.addEventListener('pointercancel', (e) => {
        if (e.pointerId !== pointerId) return;
        endDrag();
      });
    }

    document.addEventListener('gwong:variant:changed', (e) => {
      if (!shouldHandleGalleryEvent(e)) return;
      const variant = e.detail && e.detail.variant;
      if (!variant || !variant.image) return;
      applyGalleryImage(variant.image, variant.media_id);
    });

    document.addEventListener('gwong:gallery:set-hero', (e) => {
      if (!shouldHandleGalleryEvent(e)) return;
      const detail = e.detail || {};
      applyGalleryImage(detail.image, detail.media_id);
    });

    thumbs.forEach((thumb) => {
      thumb.setAttribute('role', 'tab');
    });

    window.addEventListener('resize', () => {
      if (!dragging) setTrackTransform(0, false);
    });

    updateNav();
    setTrackTransform(0, false);
    if (live) live.textContent = slideLabel(slides[index], index + 1);
  }

  function boot() {
    document.querySelectorAll('[data-pdp-gallery]').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
